import React, { useCallback, useEffect, useState } from "react";
import { FilterHeader, PackageHeaders, PackagePanels, PackageCreationDialog } from "./components";
import DraggablePackageRow from "./components/row/DraggablePackageRow";
import { PackageManagementService } from "../../../../api";
import styles from "./DSIPackages.module.css";
import { useApi } from "../../../../common/hooks";
import { createNotification } from "../../../../common/components";
import { translate } from "../../../../common/providers";

export default function DSIPackages({ platformCompanies }) {
	const { call: getAllPlatformPackages } = useApi(PackageManagementService.getAllPlatformPackages);
	const { call: getActivePackageProjects } = useApi(PackageManagementService.getActivePackageProjects);
	const { call: updatePackageOrder } = useApi(PackageManagementService.updatePackageOrder);
	const { call: updatePackage } = useApi(PackageManagementService.updatePackage);

	const [packages, setPackages] = useState([]);
	const [plName, setName] = useState("");
	const [selectedRow, setSelectedRow] = useState(null);
	const [panel, setPanel] = useState("");
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [originalIndex, setOriginalIndex] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [packageToEdit, setPackageToEdit] = useState(null);

	const handleChangeFilters = useCallback(({ name }) => {
		setName(name);
	}, []);

	useEffect(() => {
		getAllPlatformPackages(plName).then((data) => setPackages(data));
	}, [plName, getAllPlatformPackages]);
	const projectsRequest = useCallback(
		({ page, limit, search }) => {
			if (!selectedRow?.id) {
				return Promise.resolve(null);
			}
			return getActivePackageProjects({ packageId: selectedRow?.id }, { page, limit, filter: search });
		},
		[getActivePackageProjects, selectedRow?.id]
	);
	const handleSelectRow = (row, pane) => {
		if (pane === "info") {
			setPackageToEdit(row);
			setOpenDialog(true);
		} else {
			setSelectedRow(row);
			setPanel(pane);
		}
	};

	const handleClose = () => {
		setSelectedRow(null);
		setPanel("");
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setPackageToEdit(null);
	};

	const handleUpdateRow = (id, keyOrUpdates, value) => {
		setPackages((prev) =>
			prev.map((x) => {
				if (x.id !== id) {
					return x;
				}
				if (typeof keyOrUpdates === "object") {
					return { ...x, ...keyOrUpdates };
				}
				return { ...x, [keyOrUpdates]: value };
			})
		);
	};

	const handleOpenCreation = () => {
		setPackageToEdit(null);
		setOpenDialog(true);
	};

	const handleRefreshList = (newPackage) => {
		setPackages((prev) => [...prev, newPackage]);
	};

	const handleToggleForceDisplay = async (pck) => {
		const newForceDisplay = !pck.forceDisplay;
		try {
			await updatePackage({ packageId: pck.id }, { ...pck, forceDisplay: newForceDisplay });
			handleUpdateRow(pck.id, "forceDisplay", newForceDisplay);
		} catch (err) {
			console.error(err);
			createNotification({
				type: "error",
				message: translate("dsi.package-management.panel.error"),
			});
		}
	};

	const handleDragStart = (index) => {
		setDraggedIndex(index);
		setOriginalIndex(index);
	};

	const handleDragOver = (index) => {
		if (draggedIndex === null || draggedIndex === index) {
			return;
		}

		const newPackages = [...packages];
		const draggedItem = newPackages[draggedIndex];
		newPackages.splice(draggedIndex, 1);
		newPackages.splice(index, 0, draggedItem);

		setPackages(newPackages);
		setDraggedIndex(index);
	};

	const handleDragEnd = async () => {
		const movedIndex = draggedIndex;
		setDraggedIndex(null);

		if (originalIndex === null || movedIndex === null || originalIndex === movedIndex) {
			setOriginalIndex(null);
			return;
		}

		const movedPackage = packages[movedIndex];
		const previousPackage = movedIndex > 0 ? packages[movedIndex - 1] : null;

		try {
			await updatePackageOrder({
				itemId: movedPackage.id,
				previousItemId: previousPackage?.id || null,
			});
			createNotification({
				type: "success",
				message: translate("dsi.package-management.order.success"),
			});
		} catch (err) {
			console.error(err);
			getAllPlatformPackages(plName).then((data) => setPackages(data));
			createNotification({
				type: "error",
				message: translate("dsi.package-management.order.error"),
			});
		}

		setOriginalIndex(null);
	};

	return (
		<>
			<div className={styles.container}>
				<FilterHeader onChangeFilters={handleChangeFilters} onOpenCreation={handleOpenCreation} />
				<div className={styles.list}>
					<PackageHeaders />
					{packages.map((pck, index) => (
						<DraggablePackageRow
							key={pck.id}
							index={index}
							pck={pck}
							onDragEnd={handleDragEnd}
							onDragOver={handleDragOver}
							onDragStart={handleDragStart}
							onSelectRow={handleSelectRow}
							onToggleForceDisplay={handleToggleForceDisplay}
						/>
					))}
				</div>
				<PackagePanels
					open={!!panel}
					panel={panel}
					pck={selectedRow}
					platformCompanies={platformCompanies}
					projectsRequest={projectsRequest}
					onClose={handleClose}
					onRefreshList={handleRefreshList}
					onUpdateRow={handleUpdateRow}
				/>
			</div>
			<PackageCreationDialog
				open={openDialog}
				pck={packageToEdit}
				onClose={handleCloseDialog}
				onRefreshList={handleRefreshList}
				onUpdateRow={handleUpdateRow}
			/>
		</>
	);
}
